import React, { useEffect, useState } from "react";

import { Button } from "~/components/ui/button";
import { EmptyState } from "~/components/ui/empty-state";
import { Skeleton } from "~/components/ui/skeleton";
import SellerAssignmentSection from "~/pages/clients/components/client-detail/SellerAssignmentSection";
import { fetchClientFiscalAddress } from "~/services/addressService";
import { useProfileStore } from "~/store/profileStore";
import { Address, Client } from "~/types/client";

interface ClientInfoTabProps {
  client: Client;
  refreshClient?: () => void;
}

const ClientInfoTab: React.FC<ClientInfoTabProps> = ({
  client,
  refreshClient,
}) => {
  const { role } = useProfileStore();
  const isAdmin = role === "admin" || role === "superadmin";
  const [fiscalAddress, setFiscalAddress] = useState<Address | null>(null);
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);

  useEffect(() => {
    const loadFiscalAddress = async () => {
      if (client.fiscal_address_id) {
        setIsLoadingAddress(true);
        try {
          const address = await fetchClientFiscalAddress(
            client.fiscal_address_id
          );
          setFiscalAddress(address);
        } catch (error) {
          console.error("Error loading fiscal address:", error);
        } finally {
          setIsLoadingAddress(false);
        }
      }
    };

    loadFiscalAddress();
  }, [client.fiscal_address_id]);

  const handleAddressesTabClick = () => {
    const tabChangeEvent = new CustomEvent("switch-client-tab", {
      detail: { tabName: "addresses" },
    });
    window.dispatchEvent(tabChangeEvent);
  };

  return (
    <div className="space-y-6 p-4 pt-6 md:p-6">
      {isAdmin && refreshClient && (
        <SellerAssignmentSection
          client={client}
          onSellerAssigned={refreshClient}
        />
      )}

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <h3 className="text-base font-semibold">Detalles de Contacto</h3>

          <dl className="mt-3 grid grid-cols-[110px_1fr] gap-x-4 gap-y-2 text-sm">
            {client.contact_person && (
              <>
                <dt className="text-muted-foreground">Contacto</dt>
                <dd>{client.contact_person}</dd>
              </>
            )}

            {client.email && (
              <>
                <dt className="text-muted-foreground">Email</dt>
                <dd className="break-words">{client.email}</dd>
              </>
            )}

            {client.phone && (
              <>
                <dt className="text-muted-foreground">Teléfono</dt>
                <dd>{client.phone}</dd>
              </>
            )}

            {client.taxId && (
              <>
                <dt className="text-muted-foreground">RUT</dt>
                <dd>{client.taxId}</dd>
              </>
            )}

            {client.address && (
              <>
                <dt className="text-muted-foreground">Dirección</dt>
                <dd>{client.address}</dd>
              </>
            )}
          </dl>
        </div>

        <div>
          <h3 className="text-base font-semibold">Dirección Fiscal</h3>

          <div className="mt-3">
            {isLoadingAddress ? (
              <Skeleton className="h-24 w-full rounded-lg" />
            ) : fiscalAddress ? (
              <div className="space-y-1 text-sm">
                <p className="font-medium">
                  {fiscalAddress.street}, {fiscalAddress.number}
                </p>
                {fiscalAddress.complement && (
                  <p className="text-muted-foreground">
                    {fiscalAddress.complement}
                  </p>
                )}
                <p className="text-muted-foreground">
                  {fiscalAddress.comuna && `${fiscalAddress.comuna}, `}
                  {fiscalAddress.city}
                  {fiscalAddress.state && `, ${fiscalAddress.state}`}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={handleAddressesTabClick}
                >
                  Cambiar dirección fiscal
                </Button>
              </div>
            ) : (
              <EmptyState
                title="No hay dirección fiscal establecida"
                className="py-6"
                action={
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAddressesTabClick}
                  >
                    Establecer dirección fiscal
                  </Button>
                }
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientInfoTab;
